import { resources } from '../../core/services/resources';

/**
 * Frontend-executed lane's action dispatch table (§6.2) — the join point
 * between a `recommend_action` tool call's `actionId` and the app's own
 * existing controls. Kept as its own parallel list to the backend's
 * `config/actions.ts` (descriptive metadata for the model, not a callable)
 * — same "encode capabilities, not engines" caution
 * `AI-Assistant-Feature-Backend.md` §6.4 flags for `tools.ts`/`models.ts`.
 * Ids must match the backend's `AI_CHAT_ACTIONS` exactly.
 */
export interface ActionDispatchCtx {
  navigate: (path: string) => void;
}

export type ActionHandler = (
  params: Record<string, unknown> | undefined,
  ctx: ActionDispatchCtx,
) => Promise<void> | void;

function requireNumberParam(params: Record<string, unknown> | undefined, key: string): number {
  const value = params?.[key];
  if (typeof value !== 'number') throw new Error(`Missing/invalid "${key}" parameter`);
  return value;
}

const ACTION_HANDLERS: Record<string, ActionHandler> = {
  'generate-document-presentation': async (params) => {
    const documentId = requireNumberParam(params, 'documentId');
    await resources.generateDocumentPresentation(documentId);
  },
  'open-document-section': (params, ctx) => {
    const documentId = requireNumberParam(params, 'documentId');
    ctx.navigate(`/documents/${documentId}`);
  },
  'open-document-entities': (params, ctx) => {
    const documentId = requireNumberParam(params, 'documentId');
    ctx.navigate(`/documents/${documentId}/entities`);
  },
};

export async function runFrontendAction(
  actionId: string,
  params: Record<string, unknown> | undefined,
  ctx: ActionDispatchCtx,
): Promise<void> {
  const handler = ACTION_HANDLERS[actionId];
  if (!handler) throw new Error(`Unknown actionId: ${actionId}`);
  await handler(params, ctx);
}
